import type {
	CreateCheckoutSessionInput,
	CreateCheckoutSessionResponse,
	CreateCheckoutOrderInput,
	CreateCheckoutOrderResponse,
	CheckoutEmailLookupInput,
	CheckoutEmailLookupResponse,
	CheckoutVerifyOtpInput,
	CheckoutVerifyOtpResponse,
	CheckoutCustomerProfileDTO,
	CheckoutOrderDTO,
	CreatePaypalOrderInput,
	CreatePaypalOrderResponse,
	CapturePaypalOrderResponse,
	UpdatePurchaseStatusInput,
	ContactInput,
	LoginInput,
	LoginResponse,
	PurchaseWithCustomerDTO,
	PurchaseFullDTO,
	CustomerWithRelationsDTO,
	CustomerDetailDTO,
	ProductSalesRowDTO,
	PromotionDTO,
	PromotionInput,
	ActivePromotionDTO,
	CheckoutReceiptDTO,
	PurchaseSortBy,
	SortDir,
	PurchasesLimit,
	MessageDTO,
	MessageDetailDTO,
	MessageStatus,
	MessageSortBy
} from "@biosany/shared"
import { ENV } from "./env"

export class ApiError extends Error {
	constructor(
		public readonly status: number,
		message: string,
		public readonly conflicts?: Array<{ id: string; name: string; itemIds: string[] }>
	) {
		super(message)
		this.name = "ApiError"
	}
}

let authToken: string | null = null

export function setAuthToken(token: string | null): void {
	authToken = token
}

type UnauthorizedHandler = () => void
let unauthorizedHandler: UnauthorizedHandler | null = null

/** Register a handler invoked on 401 from authenticated API calls. */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
	unauthorizedHandler = handler
}

interface RequestOptions {
	method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
	body?: unknown
	auth?: boolean
	/** Bearer explícito (p. ej. sesión OTP de checkout), independiente del auth admin. */
	bearerToken?: string
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
	const { method = "GET", body, auth = false, bearerToken } = options

	const headers: Record<string, string> = {}
	if (body !== undefined) {
		headers["Content-Type"] = "application/json"
	}
	if (bearerToken) {
		headers.Authorization = `Bearer ${bearerToken}`
	} else if (auth && authToken) {
		headers.Authorization = `Bearer ${authToken}`
	}

	const response = await fetch(`${ENV.API_URL}${path}`, {
		method,
		headers,
		body: body === undefined ? undefined : JSON.stringify(body)
	})

	if (!response.ok) {
		if (auth && response.status === 401) {
			unauthorizedHandler?.()
		}

		let message = response.statusText
		let conflicts: ApiError["conflicts"]
		try {
			const data: unknown = await response.json()
			if (data && typeof data === "object") {
				const payload = data as { error?: unknown; message?: unknown; conflicts?: unknown }
				// Prefer Fastify `message` (descriptive) over generic `error` ("Bad Request").
				if (typeof payload.message === "string" && payload.message.trim()) {
					message = payload.message.trim()
				} else if (typeof payload.error === "string" && payload.error.trim()) {
					message = payload.error.trim()
				} else if (payload.error !== undefined) {
					message = JSON.stringify(payload.error)
				}
				if (Array.isArray(payload.conflicts)) {
					conflicts = payload.conflicts as ApiError["conflicts"]
				}
			}
		} catch {
			// keep statusText
		}
		throw new ApiError(response.status, message, conflicts)
	}

	if (response.status === 204) {
		return undefined as T
	}

	return (await response.json()) as T
}

export interface PurchasesParams {
	from: string
	to: string
	limit?: PurchasesLimit
	sortBy?: PurchaseSortBy
	sortDir?: SortDir
	q?: string
}

export const api = {
	createCheckoutSession(input: CreateCheckoutSessionInput): Promise<CreateCheckoutSessionResponse> {
		return request("/checkout-sessions", { method: "POST", body: input })
	},
	getCheckoutReceipt(sessionId: string): Promise<{ receipt: CheckoutReceiptDTO }> {
		return request(`/checkout-sessions/${encodeURIComponent(sessionId)}`)
	},
	checkoutEmailLookup(input: CheckoutEmailLookupInput): Promise<CheckoutEmailLookupResponse> {
		return request("/checkout/email-lookup", { method: "POST", body: input })
	},
	verifyCheckoutOtp(input: CheckoutVerifyOtpInput): Promise<CheckoutVerifyOtpResponse> {
		return request("/checkout/verify-otp", { method: "POST", body: input })
	},
	getCheckoutCustomer(token: string): Promise<{ customer: CheckoutCustomerProfileDTO }> {
		return request("/checkout/customer", { bearerToken: token })
	},
	createCheckoutOrder(input: CreateCheckoutOrderInput, checkoutToken?: string | null): Promise<CreateCheckoutOrderResponse> {
		return request("/checkout/orders", {
			method: "POST",
			body: input,
			...(checkoutToken ? { bearerToken: checkoutToken } : {})
		})
	},
	getCheckoutOrder(orderId: string): Promise<{ order: CheckoutOrderDTO }> {
		return request(`/checkout/orders/${encodeURIComponent(orderId)}`)
	},
	createPaypalOrder(input: CreatePaypalOrderInput): Promise<CreatePaypalOrderResponse> {
		return request("/checkout/paypal/orders", { method: "POST", body: input })
	},
	capturePaypalOrder(orderId: string): Promise<CapturePaypalOrderResponse> {
		return request(`/checkout/paypal/orders/${encodeURIComponent(orderId)}/capture`, { method: "POST" })
	},
	updatePurchaseStatus(id: string, input: UpdatePurchaseStatusInput): Promise<{ purchase: PurchaseFullDTO }> {
		return request(`/admin/purchases/${id}/status`, { method: "PATCH", body: input, auth: true })
	},
	submitContact(input: ContactInput): Promise<{ ok: true }> {
		return request("/contact", { method: "POST", body: input })
	},
	login(input: LoginInput): Promise<LoginResponse> {
		return request("/auth/login", { method: "POST", body: input })
	},
	getPurchases(params: PurchasesParams): Promise<{ purchases: PurchaseWithCustomerDTO[] }> {
		const q = new URLSearchParams({
			from: params.from,
			to: params.to,
			limit: String(params.limit ?? 10),
			sortBy: params.sortBy ?? "date",
			sortDir: params.sortDir ?? "desc"
		})
		if (params.q?.trim()) q.set("q", params.q.trim())
		return request(`/admin/purchases?${q}`, { auth: true })
	},
	getPurchase(id: string): Promise<{ purchase: PurchaseFullDTO }> {
		return request(`/admin/purchases/${id}`, { auth: true })
	},
	getCustomers(q?: string): Promise<{ customers: CustomerWithRelationsDTO[] }> {
		const params = new URLSearchParams()
		if (q?.trim()) params.set("q", q.trim())
		const qs = params.toString()
		return request(`/admin/customers${qs ? `?${qs}` : ""}`, { auth: true })
	},
	getCustomer(id: string): Promise<{ customer: CustomerDetailDTO }> {
		return request(`/admin/customers/${id}`, { auth: true })
	},
	getProductSales(from: string, to: string, q?: string): Promise<{ products: ProductSalesRowDTO[] }> {
		const params = new URLSearchParams({ from, to })
		if (q?.trim()) params.set("q", q.trim())
		return request(`/admin/products?${params}`, { auth: true })
	},
	getMessages(
		options: {
			from?: string
			to?: string
			q?: string
			limit?: PurchasesLimit
			sortBy?: MessageSortBy
			sortDir?: SortDir
		} = {}
	): Promise<{ messages: MessageDTO[] }> {
		const params = new URLSearchParams({
			sortBy: options.sortBy ?? "createdAt",
			sortDir: options.sortDir ?? "desc",
			limit: String(options.limit ?? 10)
		})
		if (options.from) params.set("from", options.from)
		if (options.to) params.set("to", options.to)
		if (options.q?.trim()) params.set("q", options.q.trim())
		return request(`/admin/messages?${params}`, { auth: true })
	},
	getNewMessagesCount(): Promise<{ count: number }> {
		return request("/admin/messages/new-count", { auth: true })
	},
	getMessage(id: string): Promise<{ message: MessageDetailDTO }> {
		return request(`/admin/messages/${id}`, { auth: true })
	},
	updateMessageStatus(id: string, status: MessageStatus): Promise<{ message: MessageDTO }> {
		return request(`/admin/messages/${id}`, { method: "PATCH", body: { status }, auth: true })
	},
	getPromotions(): Promise<{ promotions: PromotionDTO[] }> {
		return request("/admin/promotions", { auth: true })
	},
	createPromotion(input: PromotionInput): Promise<{ promotion: PromotionDTO }> {
		return request("/admin/promotions", { method: "POST", body: input, auth: true })
	},
	updatePromotion(id: string, input: PromotionInput): Promise<{ promotion: PromotionDTO }> {
		return request(`/admin/promotions/${id}`, { method: "PUT", body: input, auth: true })
	},
	endPromotion(id: string): Promise<{ promotion: PromotionDTO }> {
		return request(`/admin/promotions/${id}/end`, { method: "POST", auth: true })
	},
	deletePromotion(id: string): Promise<void> {
		return request(`/admin/promotions/${id}`, { method: "DELETE", auth: true })
	},
	getActivePromotions(): Promise<{ promotions: ActivePromotionDTO[] }> {
		return request<{ promotions: ActivePromotionDTO[] }>("/promotions/active").catch((err: unknown) => {
			if (err instanceof ApiError && err.status === 404) {
				return { promotions: [] }
			}
			throw err
		})
	}
}
